import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Image } from 'react-bootstrap';
import { initEcho, disconnectEcho } from '../echo';
import { useProfile } from '../context/ProfileContext';

import {
    HomeOutlined, HeartOutlined, UserOutlined, MessageOutlined,
    BellOutlined, PoweroffOutlined, LockOutlined, CheckSquareOutlined,
    SearchOutlined, FilterOutlined, UsergroupAddOutlined, SettingOutlined,
    CrownOutlined, DollarOutlined, CreditCardOutlined, LoadingOutlined,
    StopOutlined, MenuOutlined,
} from '@ant-design/icons';
import { Avatar, Modal, Popover, Spin, Empty, Drawer } from 'antd';
import 'bootstrap/dist/css/bootstrap.min.css';
import api from '../api/axios';

const NAV_ACTIVE_COLOR = '#FCD34D';      /* gold highlight for active */
const NAV_DEFAULT_COLOR = 'rgba(255,255,255,0.85)';

const CustomNavbar = ({ onNavbarReady }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [metrics, setMetrics] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const echoChannelRef = useRef(null);
    const broadcastChannelRef = useRef(null);
    const countedNotifIds = useRef(new Set());
    const { profileId, setProfile, clearProfile } = useProfile();

    const fetchMetrics = useCallback(async (profileId) => {
        try {
            const response = await api.get('/profiles/dashboard', {
                params: { profile_id: profileId },
            });
            setMetrics(response.data.data);
        } catch (error) {
            console.error('Error fetching metrics:', error);
        }
    }, []);

    const handleNotification = useCallback((payload, fromBroadcast = false) => {
        const notifId = payload.notification_id;
        // Deduplicate: skip if already counted (e.g. received via both WS and BroadcastChannel)
        if (countedNotifIds.current.has(notifId)) return;
        countedNotifIds.current.add(notifId);

        setUnreadCount((c) => c + 1);
        setNotifications((prev) => [
            {
                id: notifId,
                type: 'INFO',
                message: (() => {
                    switch (payload.type) {
                        case 'profile_viewed':      return `${payload.actor_name} viewed your profile`;
                        case 'interest_received':   return `${payload.actor_name} sent you an interest`;
                        case 'interest_accepted':   return `${payload.actor_name} accepted your interest`;
                        case 'interest_rejected':   return `${payload.actor_name} declined your interest`;
                        case 'profile_shortlisted': return `${payload.actor_name} shortlisted your profile`;
                        case 'message_received':    return `New message from ${payload.actor_name}`;
                        default: return 'You have a new notification';
                    }
                })(),
                read_at: null,
                created_at: new Date().toISOString(),
            },
            ...prev.slice(0, 7),
        ]);

        // Relay to other open tabs (only if this came via WebSocket, not already from another tab)
        if (!fromBroadcast) {
            broadcastChannelRef.current?.postMessage({ type: 'notification', payload });
        }
    }, []); // setState and refs are stable — no deps needed

    const fetchProfiles = useCallback(async () => {
        try {
            const response = await api.get('/profiles/list');
            const profilesData = response.data.data;
            const primaryProfile = profilesData.find((p) => p.is_primary) || profilesData[0];
            if (primaryProfile) {
                setSelectedProfile(primaryProfile);
                setProfile({
                    profileId:   primaryProfile.profile_id,
                    profileDbId: primaryProfile.id,
                    firstName:   primaryProfile.first_name,
                });
                await fetchMetrics(primaryProfile.profile_id);

                // Subscribe to real-time notifications for this profile
                const echo = initEcho();
                const channel = echo.private(`profile.${primaryProfile.id}`);
                echoChannelRef.current = channel;

                channel
                    .listen('.profile.viewed',      handleNotification)
                    .listen('.interest.received',   handleNotification)
                    .listen('.interest.accepted',   handleNotification)
                    .listen('.interest.rejected',   handleNotification)
                    .listen('.profile.shortlisted', handleNotification)
                    .listen('.message.received',    handleNotification);
            } else {
                navigate('/add-profile');
            }
        } catch (error) {
            console.error('Error fetching profiles:', error);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchMetrics, navigate, handleNotification]);

    const fetchNotifications = useCallback(async () => {
        if (!profileId) return;
        setNotifLoading(true);
        try {
            const response = await api.get('/notifications/show', {
                params: { profile_id: profileId, page: 1, limit: 8 },
            });
            if (response.data.status === 'success') {
                setNotifications(response.data.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setNotifLoading(false);
        }
    }, [profileId]);

    const handleNotifOpenChange = (open) => {
        setNotifOpen(open);
        if (open) {
            fetchNotifications();
            setUnreadCount(0);
        }
    };

    // Cross-tab notification relay via BroadcastChannel
    useEffect(() => {
        const bc = new BroadcastChannel('hm_notifications');
        broadcastChannelRef.current = bc;
        bc.onmessage = (e) => {
            if (e.data?.type === 'notification') {
                handleNotification(e.data.payload, true);
            }
        };
        return () => {
            bc.close();
            broadcastChannelRef.current = null;
        };
    }, [handleNotification]);

    useEffect(() => {
        const initializeNavbar = async () => {
            const userId = localStorage.getItem('user_id');
            if (!userId) { navigate('/login', { replace: true }); return; }
            await fetchProfiles();
            if (onNavbarReady) onNavbarReady();
        };
        initializeNavbar();
        return () => {
            if (echoChannelRef.current) {
                echoChannelRef.current
                    .stopListening('.profile.viewed')
                    .stopListening('.interest.received')
                    .stopListening('.interest.accepted')
                    .stopListening('.interest.rejected')
                    .stopListening('.profile.shortlisted')
                    .stopListening('.message.received');
                echoChannelRef.current = null;
            }
        };
    }, [navigate, fetchProfiles, onNavbarReady]);

    const handleLogout = async () => {
        try {
            await api.post('/logout', {});
        } catch (error) {
            console.error('Error during logout:', error);
        }
        disconnectEcho();
        clearProfile();
        localStorage.removeItem('user_id');
        localStorage.removeItem('first_name');
        localStorage.removeItem('last_name');
        localStorage.setItem('logout', Date.now());
        navigate('/');
    };

    const showModal = () => setIsModalOpen(true);
    const handleOk = () => { setIsModalOpen(false); handleLogout(); };
    const handleCancel = () => setIsModalOpen(false);

    const getInitials = (name) =>
        name.split(' ').map((w) => w[0]).join('').toUpperCase();

    /* ── helper: is given path currently active? ── */
    const isActive = (paths) =>
        Array.isArray(paths)
            ? paths.some((p) => location.pathname.startsWith(p))
            : location.pathname.startsWith(paths);

    /* ── nav link style ── */
    const navLinkStyle = (active) => ({
        color: active ? NAV_ACTIVE_COLOR : NAV_DEFAULT_COLOR,
        fontWeight: active ? '600' : '400',
        fontSize: '0.88rem',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '6px 12px',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
    });

    /* ── badge for counts ── */
    const CountBadge = ({ count }) =>
        count > 0 ? (
            <span style={{
                background: '#EF4444', color: 'white',
                borderRadius: '9999px', padding: '1px 6px',
                fontSize: '0.68rem', fontWeight: '700',
                minWidth: '18px', textAlign: 'center',
                lineHeight: '16px', display: 'inline-block',
                marginLeft: '3px',
                boxShadow: '0 2px 6px rgba(239,68,68,0.4)',
            }}>
                {count}
            </span>
        ) : null;

    const TYPE_BG = {
        MESSAGE: '#0891B2', ALERT: '#EF4444', INVITE: '#10B981', INFO: '#F59E0B',
    };

    const notifPopoverContent = (
        <div style={{ width: '340px', fontFamily: 'Calibri, sans-serif' }}>
            {notifLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD1457' }} spin />} />
                </div>
            ) : notifications.length === 0 ? (
                <div style={{ padding: '24px 0' }}>
                    <Empty description={<span style={{ color: '#94A3B8', fontSize: '0.88rem' }}>No notifications</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            ) : (
                <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                    {notifications.map((item, i) => {
                        const bg = TYPE_BG[item.type] || '#AD1457';
                        return (
                            <div key={i} style={{
                                display: 'flex', gap: '12px', alignItems: 'flex-start',
                                padding: '10px 4px',
                                borderBottom: i < notifications.length - 1 ? '1px solid #F1F5F9' : 'none',
                            }}>
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                                    background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <BellOutlined style={{ color: 'white', fontSize: '0.9rem' }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.82rem', color: '#1A1A2E', lineHeight: '1.45', marginBottom: '3px' }}>
                                        {item.message}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                                        {new Date(item.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    return (
        <>
            <Navbar
                expand="lg"
                fixed="top"
                variant="dark"
                style={{
                    background: 'linear-gradient(135deg, #880E6E 0%, #AD1457 100%)',
                    fontFamily: 'Calibri, sans-serif',
                    padding: '0.5rem 1rem',
                    boxShadow: '0 2px 20px rgba(173,20,87,0.3)',
                    minHeight: '64px',
                }}
            >
                {/* Brand */}
                <Navbar.Brand
                    as={Link}
                    to="/dashboard"
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/oottan_com_white_background.png`}
                        alt="Oottan"
                        style={{ height: '44px', width: 'auto', objectFit: 'contain' }}
                    />
                </Navbar.Brand>

                {/* Mobile hamburger — opens sidebar */}
                <button
                    className="d-lg-none"
                    onClick={() => setSidebarOpen(true)}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                        color: '#ffffff', fontSize: '1.4rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <MenuOutlined />
                </button>

                {/* Desktop collapse only */}
                <Navbar.Collapse id="basic-navbar-nav" className="d-none d-lg-flex">
                    {/* ── Primary Nav ── */}
                    <Nav className="me-auto align-items-center" style={{ gap: '2px' }}>

                        <Nav.Link
                            as={Link}
                            to="/dashboard"
                            style={navLinkStyle(isActive('/dashboard'))}
                        >
                            <HomeOutlined /> Home
                        </Nav.Link>

                        <Nav.Link
                            as={Link}
                            to="/matches"
                            style={navLinkStyle(isActive('/matches'))}
                        >
                            <HeartOutlined /> Matches
                        </Nav.Link>

                        {/* Requests dropdown */}
                        <NavDropdown
                            title={
                                <span style={{ ...navLinkStyle(isActive('/requests')), display: 'inline-flex' }}>
                                    <UserOutlined /> Requests
                                    <CountBadge count={metrics.received_invites} />
                                </span>
                            }
                            id="requests-dropdown"
                            menuVariant="light"
                        >
                            <NavDropdown.Item as={Link} to="/requests/sent">
                                Sent <CountBadge count={metrics.sent_invites} />
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/requests/received">
                                Received <CountBadge count={metrics.received_invites} />
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/requests/accepted">
                                Accepted <CountBadge count={metrics.accepted_requests} />
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/requests/rejected">
                                Rejected <CountBadge count={metrics.rejected_requests} />
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/requests/cancelled">
                                Cancelled <CountBadge count={metrics.cancelled_requests} />
                            </NavDropdown.Item>
                        </NavDropdown>

                        {/* Shortlists dropdown */}
                        <NavDropdown
                            title={
                                <span style={{ ...navLinkStyle(isActive('/shortlists')), display: 'inline-flex' }}>
                                    <CheckSquareOutlined /> Shortlists
                                    <CountBadge count={metrics.shortlisted_by_others} />
                                </span>
                            }
                            id="shortlists-dropdown"
                            menuVariant="light"
                        >
                            <NavDropdown.Item as={Link} to="/shortlists/by-me">
                                Shortlisted by me <CountBadge count={metrics.shortlisted_by_me} />
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/shortlists/by-others">
                                Others who shortlisted me <CountBadge count={metrics.shortlisted_by_others} />
                            </NavDropdown.Item>
                        </NavDropdown>

                        {/* Blocked Profiles */}
                        <Nav.Link
                            as={Link}
                            to="/blocked-profiles"
                            style={navLinkStyle(isActive('/blocked-profiles'))}
                        >
                            <StopOutlined /> Blocked
                        </Nav.Link>

                        {/* Search dropdown */}
                        <NavDropdown
                            title={
                                <span style={{ ...navLinkStyle(isActive('/search')), display: 'inline-flex' }}>
                                    <SearchOutlined /> Search
                                </span>
                            }
                            id="search-dropdown"
                            menuVariant="light"
                        >
                            <NavDropdown.Item as={Link} to="/search/by-id">
                                Search by Profile ID
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/search/advanced">
                                Advanced Search
                            </NavDropdown.Item>
                        </NavDropdown>

                        {/* Inbox */}
                        <Nav.Link
                            as={Link}
                            to="/inbox"
                            style={navLinkStyle(isActive('/inbox'))}
                        >
                            <MessageOutlined />
                            {' '}Inbox
                            <CountBadge count={metrics.unread_messages} />
                        </Nav.Link>

                        {/* Notifications popover */}
                        <Popover
                            content={notifPopoverContent}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Calibri, sans-serif', padding: '2px 0' }}>
                                    <span style={{ fontWeight: '700', color: '#1A1A2E' }}>Notifications</span>
                                    {metrics.unread_notifications > 0 && (
                                        <span style={{ fontSize: '0.75rem', color: '#AD1457', fontWeight: '600' }}>
                                            {metrics.unread_notifications} unread
                                        </span>
                                    )}
                                </div>
                            }
                            trigger="click"
                            open={notifOpen}
                            onOpenChange={handleNotifOpenChange}
                            placement="bottomRight"
                            overlayInnerStyle={{ padding: '12px 16px', borderRadius: '14px' }}
                            overlayStyle={{ zIndex: 1050 }}
                        >
                            <span style={{ ...navLinkStyle(notifOpen), cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                <BellOutlined />
                                {' '}Alerts
                                <CountBadge count={(metrics.unread_notifications || 0) + unreadCount} />
                            </span>
                        </Popover>
                    </Nav>

                    {/* ── User Dropdown ── */}
                    <Nav className="align-items-center">
                        <NavDropdown
                            align="end"
                            title={
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    color: 'white', fontSize: '0.9rem', fontWeight: '500',
                                }}>
                                    {selectedProfile?.profile_pictures?.length > 0 &&
                                     selectedProfile.profile_pictures[0]?.image_path ? (
                                        <Image
                                            src={`${process.env.REACT_APP_IMAGE_URL}/${selectedProfile.profile_pictures[0].image_path}`}
                                            alt="Profile"
                                            roundedCircle
                                            style={{
                                                width: '32px', height: '32px',
                                                objectFit: 'cover',
                                                border: '2px solid rgba(255,255,255,0.4)',
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            style={{
                                                background: 'linear-gradient(135deg,#D81B60,#A78BFA)',
                                                width: '32px', height: '32px',
                                                fontSize: '0.8rem', fontWeight: '700',
                                                border: '2px solid rgba(255,255,255,0.3)',
                                            }}
                                        >
                                            {getInitials(
                                                selectedProfile
                                                    ? `${selectedProfile.first_name} ${selectedProfile.last_name}`
                                                    : 'User'
                                            )}
                                        </Avatar>
                                    )}
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                        {selectedProfile
                                            ? `${selectedProfile.first_name} ${selectedProfile.last_name}`
                                            : 'User'}
                                    </span>
                                </span>
                            }
                            id="user-dropdown"
                            menuVariant="light"
                        >
                            <NavDropdown.Item as={Link} to={`/profile?id=${profileId}`}>
                                <UserOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                My Profile
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/partner-preferences">
                                <UsergroupAddOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                Partner Preferences
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/account-settings">
                                <SettingOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                Account Settings
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/change-password">
                                <LockOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                Change Password
                            </NavDropdown.Item>

                            <NavDropdown.Divider />

                            <NavDropdown.Item
                                as={Link}
                                to="/pricing"
                                style={{ color: '#D97706', fontWeight: '600' }}
                            >
                                <CrownOutlined style={{ marginRight: '8px', color: '#F59E0B' }} />
                                Buy Premium
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/subscription-management">
                                <CreditCardOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                My Subscription
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/payment-history">
                                <DollarOutlined style={{ marginRight: '8px', color: '#AD1457' }} />
                                Payment History
                            </NavDropdown.Item>

                            <NavDropdown.Divider />

                            <NavDropdown.Item
                                onClick={showModal}
                                style={{ color: '#EF4444', fontWeight: '500' }}
                            >
                                <PoweroffOutlined style={{ marginRight: '8px', color: '#EF4444' }} />
                                Logout
                            </NavDropdown.Item>
                        </NavDropdown>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>

            {/* ── Mobile Sidebar ── */}
            <Drawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                placement="left"
                width={272}
                closeIcon={null}
                title={null}
                styles={{
                    body: { padding: 0, display: 'flex', flexDirection: 'column', background: '#FFFAFC' },
                    wrapper: { boxShadow: '4px 0 32px rgba(173,20,87,0.18)' },
                }}
            >
                {/* Sidebar header */}
                <div style={{
                    background: 'linear-gradient(135deg, #880E6E 0%, #AD1457 100%)',
                    padding: '20px 16px 16px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/oottan_com_white_background.png`}
                            alt="Oottan"
                            style={{ height: '38px', width: 'auto', objectFit: 'contain' }}
                        />
                        <button
                            onClick={() => setSidebarOpen(false)}
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', color: 'white', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            ✕
                        </button>
                    </div>
                    {/* Profile info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {selectedProfile?.profile_pictures?.[0]?.image_path ? (
                            <img
                                src={`${process.env.REACT_APP_IMAGE_URL}/${selectedProfile.profile_pictures[0].image_path}`}
                                alt="profile"
                                style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)' }}
                            />
                        ) : (
                            <Avatar style={{ background: 'linear-gradient(135deg,#D81B60,#A78BFA)', width: '44px', height: '44px', fontSize: '1rem', fontWeight: '700', border: '2px solid rgba(255,255,255,0.3)' }}>
                                {getInitials(selectedProfile ? `${selectedProfile.first_name} ${selectedProfile.last_name}` : 'U')}
                            </Avatar>
                        )}
                        <div>
                            <div style={{ color: 'white', fontWeight: '600', fontSize: '0.92rem' }}>
                                {selectedProfile ? `${selectedProfile.first_name} ${selectedProfile.last_name}` : 'User'}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem' }}>{profileId}</div>
                        </div>
                    </div>
                </div>

                {/* Nav links */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {[
                        { to: '/dashboard',        icon: <HomeOutlined />,          label: 'Home' },
                        { to: '/matches',          icon: <HeartOutlined />,         label: 'Matches' },
                        { to: '/requests/received', icon: <UserOutlined />,         label: 'Requests', badge: metrics.received_invites },
                        { to: '/shortlists/by-me', icon: <CheckSquareOutlined />,   label: 'Shortlists', badge: metrics.shortlisted_by_others },
                        { to: '/blocked-profiles', icon: <StopOutlined />,          label: 'Blocked Profiles' },
                        { to: '/search/by-id',     icon: <SearchOutlined />,        label: 'Search by ID' },
                        { to: '/search/advanced',  icon: <FilterOutlined />,        label: 'Advanced Search' },
                        { to: '/inbox',            icon: <MessageOutlined />,       label: 'Inbox', badge: metrics.unread_messages },
                        { to: '/notifications',    icon: <BellOutlined />,          label: 'Notifications', badge: (metrics.unread_notifications || 0) + unreadCount },
                    ].map(({ to, icon, label, badge }) => (
                        <Link
                            key={to}
                            to={to}
                            onClick={() => setSidebarOpen(false)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '11px 20px', textDecoration: 'none',
                                color: location.pathname.startsWith(to) ? '#AD1457' : '#374151',
                                fontWeight: location.pathname.startsWith(to) ? '600' : '400',
                                background: location.pathname.startsWith(to) ? 'rgba(173,20,87,0.07)' : 'transparent',
                                borderLeft: location.pathname.startsWith(to) ? '3px solid #AD1457' : '3px solid transparent',
                                fontSize: '0.9rem', fontFamily: 'Calibri, sans-serif',
                                transition: 'all 0.15s',
                            }}
                        >
                            <span style={{ fontSize: '1rem', width: '18px', textAlign: 'center' }}>{icon}</span>
                            {label}
                            {badge > 0 && (
                                <span style={{ marginLeft: 'auto', background: '#EF4444', color: 'white', borderRadius: '9999px', padding: '1px 7px', fontSize: '0.68rem', fontWeight: '700' }}>
                                    {badge}
                                </span>
                            )}
                        </Link>
                    ))}

                    <div style={{ height: '1px', background: '#F1F5F9', margin: '8px 20px' }} />

                    {[
                        { to: `/profile?id=${profileId}`, icon: <UserOutlined />,          label: 'My Profile' },
                        { to: '/partner-preferences',     icon: <UsergroupAddOutlined />,   label: 'Partner Preferences' },
                        { to: '/account-settings',        icon: <SettingOutlined />,        label: 'Account Settings' },
                        { to: '/change-password',         icon: <LockOutlined />,           label: 'Change Password' },
                        { to: '/pricing',                 icon: <CrownOutlined />,          label: 'Buy Premium', gold: true },
                        { to: '/subscription-management', icon: <CreditCardOutlined />,     label: 'My Subscription' },
                        { to: '/payment-history',         icon: <DollarOutlined />,         label: 'Payment History' },
                    ].map(({ to, icon, label, gold }) => (
                        <Link
                            key={to}
                            to={to}
                            onClick={() => setSidebarOpen(false)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '10px 20px', textDecoration: 'none',
                                color: gold ? '#D97706' : '#374151',
                                fontWeight: gold ? '600' : '400',
                                fontSize: '0.88rem', fontFamily: 'Calibri, sans-serif',
                            }}
                        >
                            <span style={{ fontSize: '0.95rem', width: '18px', textAlign: 'center', color: gold ? '#F59E0B' : '#AD1457' }}>{icon}</span>
                            {label}
                        </Link>
                    ))}
                </div>

                {/* Logout */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #F1F5F9' }}>
                    <button
                        onClick={() => { setSidebarOpen(false); showModal(); }}
                        style={{
                            width: '100%', padding: '10px', border: '1px solid rgba(239,68,68,0.2)',
                            borderRadius: '10px', background: 'rgba(239,68,68,0.06)',
                            color: '#EF4444', fontWeight: '600', fontSize: '0.9rem',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: '8px', fontFamily: 'Calibri, sans-serif',
                        }}
                    >
                        <PoweroffOutlined /> Logout
                    </button>
                </div>
            </Drawer>

            {/* ── Logout confirmation ── */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: 'rgba(239,68,68,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <PoweroffOutlined style={{ color: '#EF4444', fontSize: '1rem' }} />
                        </div>
                        <span style={{ fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>
                            Confirm Logout
                        </span>
                    </div>
                }
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okText="Yes, Logout"
                cancelText="Stay"
                centered
                okButtonProps={{
                    danger: true,
                    style: { borderRadius: '8px', fontWeight: '600', height: '36px' },
                }}
                cancelButtonProps={{
                    style: { borderRadius: '8px', height: '36px' },
                }}
            >
                <p style={{ fontFamily: 'Calibri, sans-serif', color: '#64748B', margin: '8px 0' }}>
                    Are you sure you want to logout from Oottan?
                </p>
            </Modal>
        </>
    );
};

export default CustomNavbar;
